// S8i (2026-06-03) — Custom-block subtype metadata.
//
// `data.blockType` on a CUSTOM block selects the runtime contract
// the engine applies. There are TWO families:
//
//   • PER-BAR (indicator / pattern / analysis / filter /
//     signal_generator) — the worker calls `calculate(...)` once
//     per bar; the block emits a BlockOutput { values, signals }
//     that downstream graph consumers (Expression, LogicGate,
//     terminals) read.
//
//   • PER-TRADE (qty_calculator / sl_calculator / tp_calculator /
//     risk_rule) — the engine SKIPS the per-bar pre-compute pass
//     (customBlockRuntime.ts:190 PER_TRADE_BLOCK_TYPES) and
//     instead invokes a specific named function on the user's
//     code at trade lifecycle points (entry sizing, SL resolve,
//     TP resolve, risk checks). No BlockOutput is produced.
//
// Pre-S8i the Custom Block Editor's Block Type dropdown listed
// ONLY the per-bar subtypes. A `qty_calculator` block (e.g. the
// FTMO calcQty migration 0034) loaded into the editor showed
// "Indicator" because that value isn't in the options list —
// the browser fell back to the first <option>. This silently
// mislabeled the block's runtime semantics to the user, who
// then had no surface explaining how a calcQty's return value
// reaches the Long Entry's sizing.
//
// This module is the single source of truth for:
//   • the canonical subtype list (per family)
//   • display labels for each subtype
//   • runtime contract documentation (signature + summary)
//   • the helpers/ctx/params API reference each subtype exposes
//
// Consumed by CustomBlockEditor.tsx (modal Block Type dropdown
// + info cards) AND BlockEditor.tsx (right-panel "Block role"
// badge).

export const PER_BAR_SUBTYPES = [
  'indicator',
  'pattern',
  'analysis',
  'filter',
  'signal_generator',
] as const;

export const PER_TRADE_SUBTYPES = [
  'qty_calculator',
  'sl_calculator',
  'tp_calculator',
  'risk_rule',
] as const;

export type PerBarSubtype = typeof PER_BAR_SUBTYPES[number];
export type PerTradeSubtype = typeof PER_TRADE_SUBTYPES[number];
export type CustomBlockSubtype = PerBarSubtype | PerTradeSubtype;

/** Short, user-facing label. Per-trade subtypes carry the
 *  "(per-trade)" suffix so the dropdown disambiguates them from
 *  the per-bar family at a glance. */
const LABELS: Record<CustomBlockSubtype, string> = {
  indicator: 'Indicator',
  pattern: 'Pattern',
  analysis: 'Analysis',
  filter: 'Filter',
  signal_generator: 'Signal Generator',
  qty_calculator: 'Quantity Calculator (per-trade)',
  sl_calculator: 'Stop-Loss Calculator (per-trade)',
  tp_calculator: 'Take-Profit Calculator (per-trade)',
  risk_rule: 'Risk Rule (per-trade)',
};

export function subtypeLabel(subtype: string): string {
  return LABELS[subtype as CustomBlockSubtype] ?? subtype;
}

export function isPerBarSubtype(subtype: string): boolean {
  return (PER_BAR_SUBTYPES as readonly string[]).includes(subtype);
}

export function isPerTradeSubtype(subtype: string): boolean {
  return (PER_TRADE_SUBTYPES as readonly string[]).includes(subtype);
}

/** Per-trade contract metadata — used by the modal's info card
 *  AND the BlockEditor's role badge. `fnName` is the EXACT
 *  identifier the engine looks for in the user's code (e.g.
 *  qtyCalculator.ts:139 throws if `typeof computeQty !==
 *  'function'`). `signature` is the human-readable type sketch.
 *  `summary` is one paragraph explaining when the engine fires
 *  this function and what the return value is consumed for.
 *  `pointerField` names the data.*BlockId slot on the ENTRY (or
 *  other host block) that activates dispatch — without that
 *  pointer set, the block is dormant. */
export interface PerTradeContract {
  fnName: string;
  signature: string;
  summary: string;
  pointerField: string | null;
  pointerHost: string;
}

const PER_TRADE_CONTRACTS: Record<PerTradeSubtype, PerTradeContract> = {
  qty_calculator: {
    fnName: 'qty',
    signature: 'qty_calculator { qty: <expression over ctx + params> } → number',
    summary:
      'Engine evaluates the block\'s safe-TSL `qty:` expression once per trade at entry ' +
      'time to size the position (ctx fields: equity, dayStartEquity, initialCapital, ' +
      'entryPrice, stopDistance, atr, price). The engine then applies max_notional_usd / ' +
      'max_leverage / max_position_size hard caps on top. Wins over the entry block\'s ' +
      'inline quantity_type / quantity_value fields when the entry references this block ' +
      'via qtyCalculatorBlockId. To wire it: draw a canvas edge from this block to the ' +
      'ENTRY — the qtyCalculatorBlockId pointer is set automatically (S8j). Well-known ' +
      'implementations can instead dispatch natively via a parameters.__canonical marker ' +
      '(e.g. "ftmo_calcqty_v1").',
    pointerField: 'qtyCalculatorBlockId',
    pointerHost: 'ENTRY block',
  },
  sl_calculator: {
    fnName: '__canonical',
    signature: 'parameters.__canonical = "ftmo_calcsl_v1" → native stop-loss price',
    summary:
      'Canonical-only: stop-loss calculators run as trusted native engine implementations ' +
      'selected by the parameters.__canonical marker — there is no inline TSL body for this ' +
      'subtype. Computes the stop-loss PRICE once per trade at entry time; wins over the ' +
      'STOP_LOSS terminal block\'s stopType / stopValue when the entry references this block ' +
      'via slCalculatorBlockId. To wire it: draw a canvas edge from this block to the ENTRY — ' +
      'the slCalculatorBlockId pointer is set automatically (S8j). For custom stop math, use ' +
      'the STOP_LOSS terminal\'s built-in modes instead.',
    pointerField: 'slCalculatorBlockId',
    pointerHost: 'ENTRY block',
  },
  tp_calculator: {
    fnName: '__canonical',
    signature: 'parameters.__canonical = "ftmo_calctp_v1" → native take-profit price',
    summary:
      'Canonical-only: take-profit calculators run as trusted native engine implementations ' +
      'selected by the parameters.__canonical marker — there is no inline TSL body for this ' +
      'subtype. Computes the take-profit PRICE once per trade at entry time; wins over the ' +
      'TAKE_PROFIT terminal block\'s tpType / tpValue when the entry references this block ' +
      'via tpCalculatorBlockId. To wire it: draw a canvas edge from this block to the ENTRY — ' +
      'the tpCalculatorBlockId pointer is set automatically (S8j). For custom target math, use ' +
      'the TAKE_PROFIT terminal\'s built-in modes instead.',
    pointerField: 'tpCalculatorBlockId',
    pointerHost: 'ENTRY block',
  },
  risk_rule: {
    fnName: 'risk',
    signature: 'risk_rule { risk: <action-code expression> } → 0 allow · 1 block_entry · 2 flatten',
    summary:
      'Engine evaluates the block\'s safe-TSL `risk:` expression once per bar while a ' +
      'position is open (ctx fields: barsSinceEntry, entryPrice, barClose, barHigh, barLow; ' +
      'the bar stream restarts at each entry). The numeric result is an action code: 0 allow-' +
      'through, 1 block_entry, 2 flatten. Dispatched by presence in the strategy (no pointer ' +
      'field — the engine scans for risk_rule blocks). Prop-firm rules (daily loss, trailing ' +
      'drawdown, session windows, …) dispatch natively via parameters.__canonical markers.',
    pointerField: null,
    pointerHost: 'all entries (presence-detected)',
  },
};

export function perTradeContract(
  subtype: string,
): PerTradeContract | null {
  return PER_TRADE_CONTRACTS[subtype as PerTradeSubtype] ?? null;
}

/** S10q (2026-06-03) — Apply a user's data.entryFunctionName
 *  override to a PerTradeContract so the modal + BlockEditor
 *  sidebar text reflect the renamed function instead of the
 *  hardcoded canonical (e.g. user typed "computeQty2" → the
 *  contract card shows "computeQty2(ctx, params, helpers)" and
 *  "Your code MUST define function computeQty2(...)").
 *
 *  Caveats:
 *    • risk_rule has a MULTI-METHOD contract and doesn't take an
 *      entryFunctionName override (engine: slTpCalculator.ts does
 *      not surface one); callers should pass undefined / empty
 *      string for risk_rule and this returns the contract
 *      unchanged.
 *    • The `summary` text stays intact — it references engine
 *      field names (qtyCalculatorBlockId etc.) which are NOT
 *      user-renamable.
 *    • Empty / whitespace-only override is a no-op; we fall back
 *      to the canonical the engine would use.
 *
 *  Mutation-free — returns a NEW object when applied, the same
 *  reference when not. */
export function applyFnNameOverride(
  contract: PerTradeContract,
  overrideName?: string,
): PerTradeContract {
  // TSL migration: entryFunctionName was a host-JS-era concept (renaming the
  // computeQty/computeSl/computeTp entry point in `data.code`). Host JS is no
  // longer executed anywhere — per-trade blocks run via a safe-TSL section or a
  // parameters.__canonical native marker, neither of which has a function name
  // to override. Kept as an identity so callers (BlockEditor badge) need no
  // signature change; any stale data.entryFunctionName field is simply ignored.
  void overrideName;
  return contract;
}

/** TSL ctx-field docs, per subtype family. These mirror the engine's
 *  authoritative lists in src/engine/tslBlock/ast.ts (SIZING_CTX /
 *  RISK_CTX) — every field here is readable by name inside the block's
 *  `qty:` / `risk:` expression. Per-bar blocks read OHLCV sources
 *  (open/high/low/close/volume/hl2/hlc3/ohlc4) instead of ctx. */
export interface CtxFieldDoc {
  name: string;
  type: string;
  description: string;
}

export const SIZING_CTX_DOCS: readonly CtxFieldDoc[] = Object.freeze([
  { name: 'equity', type: 'number', description: 'Current account equity at the moment of entry (startingEquity + realized PnL).' },
  { name: 'dayStartEquity', type: 'number', description: 'Equity at the start of the current trading day (for MDL headroom math).' },
  { name: 'initialCapital', type: 'number', description: 'The strategy_config.initial_capital seed value.' },
  { name: 'entryPrice', type: 'number', description: 'The fill price the engine is about to enter at.' },
  { name: 'stopDistance', type: 'number', description: 'Distance from entryPrice to the resolved stop-loss, in instrument points. Used to size by risk.' },
  { name: 'atr', type: 'number', description: 'Current-bar ATR. NaN during warmup — guard with nz(...) or a comparison.' },
  { name: 'price', type: 'number', description: 'Current price (same as entryPrice at the sizing call site).' },
]);

export const RISK_CTX_DOCS: readonly CtxFieldDoc[] = Object.freeze([
  { name: 'barsSinceEntry', type: 'number', description: 'Bars the position has been open (0 on the entry bar). The risk stream restarts at each entry.' },
  { name: 'entryPrice', type: 'number', description: 'The open position\'s fill price.' },
  { name: 'barClose', type: 'number', description: 'Current bar close.' },
  { name: 'barHigh', type: 'number', description: 'Current bar high.' },
  { name: 'barLow', type: 'number', description: 'Current bar low.' },
]);

// ── S8j (2026-06-03) — Canvas-edge auto-wiring for per-trade dispatch ──
//
// Pre-S8j a user building a strategy manually had NO discoverable
// way to wire a qty_calculator block to an entry: the engine
// dispatches via `entry.data.qtyCalculatorBlockId` (a string id
// pointer set by migration JSON), and the canvas edge between the
// two blocks was decorative. Without JSON edit access, the per-
// trade contract was unreachable except via templates.
//
// S8j makes the canvas edge first-class for per-trade dispatch:
// when the user draws an edge from a per-trade calc block to an
// ENTRY, the corresponding *CalculatorBlockId field is also set
// on the entry. Removing the edge clears the pointer. The data
// pointer remains the authoritative source the engine reads —
// the canvas edge merely WRITES it.
//
// Pure helper exported here so BlockCanvas's connection +
// disconnection handlers + the conversion-test bundle can share
// the same registry.

import type { Block } from '../../types';
import { BlockType } from '../../types';

/** Map per-trade subtype → the data.* field on the ENTRY that
 *  activates dispatch. Derived from the perTradeContract
 *  registry above so they stay in lockstep — a single source of
 *  truth. risk_rule has no pointer field (presence-detected). */
function dispatchFieldFor(subtype: string): string | null {
  const c = perTradeContract(subtype);
  return c?.pointerField ?? null;
}

/** Returns the entry-data field that should be set when an edge
 *  is drawn from `source` to a target ENTRY block. Returns null
 *  when:
 *    • source isn't a CUSTOM block, OR
 *    • source's blockType isn't a per-trade subtype with a
 *      pointer field (per-bar subtypes / risk_rule return null).
 *  Callers must ALSO verify the target is an ENTRY block before
 *  applying the wire — this helper only inspects the source side. */
export function getCustomBlockDispatchField(source: Block): string | null {
  if (source.type !== BlockType.CUSTOM) return null;
  const subtype = String(
    (source.data as Record<string, unknown> | undefined)?.blockType ?? ''
  );
  return dispatchFieldFor(subtype);
}

/** Apply the auto-wire side effect for an edge being CREATED
 *  from `sourceId` to `targetId`. Returns the (possibly updated)
 *  target block ready to be spliced into the strategy. Mutation-
 *  free: returns a NEW object when wiring applies, the same
 *  reference when it doesn't.
 *
 *  Wiring applies when ALL of:
 *    1. source is a CUSTOM block with a per-trade pointer subtype.
 *    2. target is an ENTRY block.
 *
 *  The field is written unconditionally (last-write-wins). If the
 *  user had a stale pointer from a previous edge / JSON, the new
 *  edge takes priority. */
export function applyDispatchAutoWire(
  source: Block,
  target: Block,
): Block {
  if (target.type !== BlockType.ENTRY) return target;
  const field = getCustomBlockDispatchField(source);
  if (!field) return target;
  return {
    ...target,
    data: { ...target.data, [field]: source.id },
  };
}

/** Apply the auto-unwire side effect for an edge being REMOVED
 *  from `sourceId` to `targetId`. If the target carries a
 *  pointer field that currently points at the SOURCE id, the
 *  field is cleared. If the field points elsewhere (e.g. the
 *  user has multiple calc blocks wired and is removing one of
 *  the older edges), it's left alone — the live pointer wins.
 *
 *  This matches the symmetric "last-write-wins" semantics of
 *  applyDispatchAutoWire. */
export function applyDispatchAutoUnwire(
  source: Block,
  target: Block,
): Block {
  if (target.type !== BlockType.ENTRY) return target;
  const field = getCustomBlockDispatchField(source);
  if (!field) return target;
  const data = target.data as Record<string, unknown> | undefined;
  if (!data || data[field] !== source.id) return target;
  const nextData = { ...data };
  delete nextData[field];
  return { ...target, data: nextData as Block['data'] };
}

// ── S10c (2026-06-03) — Expression operand auto-wiring ──────────────────
//
// Pre-S10c drawing a canvas edge from an indicator to an Expression
// updated `connections.inputs` but did NOT set
// `leftInputBlockId` / `rightInputBlockId` on the Expression. The
// user had to manually pick from a dropdown afterwards — and if
// they didn't, the engine silently read NaN.
//
// S10c mirrors the S8j pattern for Expression's operand pointers.
// Strategy: assign the FIRST edge to leftInputBlockId (or
// leftPriceDataBlockId for PRICE_DATA sources), the SECOND to
// rightInputBlockId / rightPriceDataBlockId, and the THIRD+ are
// available via the dropdown for explicit reassignment.
//
// The "first/second" determination uses whichever pointer slot is
// currently UNSET. If both slots are filled, the auto-wire is a
// no-op and the user can re-pick from the dropdown. Conservative
// by design — never overwrite an existing pointer.

/** Apply Expression-operand auto-wiring when an edge is created
 *  from `source` to `target`. Returns target unchanged when:
 *    • target isn't an EXPRESSION block.
 *    • the appropriate pointer slot is already populated.
 *
 *  PRICE_DATA sources prefer the *PriceDataBlockId slots (since
 *  that's the only place price-data blocks can be assigned). All
 *  other source types prefer the *InputBlockId slots. */
export function applyExpressionOperandAutoWire(
  source: Block,
  target: Block,
): Block {
  if (target.type !== BlockType.EXPRESSION) return target;
  // target is a real EXPRESSION Block, so its data is a populated BlockData
  // (keeps the required `name` through the spreads below). The Expression
  // operand fields read here are all declared BlockData members.
  const data = target.data;

  // S10n (2026-06-03) — F51 fix. When rightSource is 'range_input'
  // (dynamic range operand for inside_range / outside_range
  // operators), the user needs to wire TWO upstream blocks: one
  // for the low bound, one for the high bound. Engine reads
  // rightLowInputBlockId + rightHighInputBlockId at graph.ts:483-485.
  // Mirror the same "first-empty-slot-wins" pattern: first edge
  // → rightLowInputBlockId; second → rightHighInputBlockId.
  // Subsequent edges become no-ops (manual dropdown for swap).
  if (data.rightSource === 'range_input' && source.type !== BlockType.PRICE_DATA) {
    if (!data.rightLowInputBlockId) {
      return { ...target, data: { ...data, rightLowInputBlockId: source.id } };
    }
    if (!data.rightHighInputBlockId) {
      return { ...target, data: { ...data, rightHighInputBlockId: source.id } };
    }
    return target;
  }

  const isPriceData = source.type === BlockType.PRICE_DATA;
  const [leftField, rightField] = isPriceData
    ? (['leftPriceDataBlockId', 'rightPriceDataBlockId'] as const)
    : (['leftInputBlockId', 'rightInputBlockId'] as const);

  if (!data[leftField]) {
    return { ...target, data: { ...data, [leftField]: source.id } };
  }
  if (!data[rightField]) {
    return { ...target, data: { ...data, [rightField]: source.id } };
  }
  // Both slots populated — leave alone (last-write-wins is bad
  // here because the user might have explicitly picked from the
  // dropdown after wiring multiple blocks).
  return target;
}

/** Apply Expression-operand auto-unwiring when an edge is removed.
 *  Clears whichever of the FOUR operand pointer slots currently
 *  points at `source.id`. The dropdown remains available for
 *  the user to re-pick from the remaining connected blocks. */
export function applyExpressionOperandAutoUnwire(
  source: Block,
  target: Block,
): Block {
  if (target.type !== BlockType.EXPRESSION) return target;
  const data = (target.data ?? {}) as Record<string, unknown>;
  // S10n — F51 fix mirror. Range_input low/high pointers join
  // the unwire list so removing an edge clears whichever bound
  // pointed at the source.
  const fields = [
    'leftInputBlockId',
    'rightInputBlockId',
    'leftPriceDataBlockId',
    'rightPriceDataBlockId',
    'rightLowInputBlockId',
    'rightHighInputBlockId',
  ];
  let changed = false;
  const nextData = { ...data };
  for (const f of fields) {
    if (nextData[f] === source.id) {
      delete nextData[f];
      changed = true;
    }
  }
  if (!changed) return target;
  return { ...target, data: nextData as Block['data'] };
}

// ── TSL migration — Safe-TSL starter templates ─────────────────────────
//
// The Builder's custom-block authoring surface is TSL-only: host JavaScript
// is never executed by the engine (it is quarantined at run time), so the
// editor neither creates nor edits `data.code`. Starters are canonical TSL
// text (src/engine/tslBlock/blockText.ts grammar) — parsed by parseBlock at
// module load so a broken template is a build error, never a dead block.
//
// sl_calculator / tp_calculator have NO inline TSL body (the engine runs
// them only as trusted native implementations selected by a
// parameters.__canonical marker) → no starter; the editor explains and
// offers the canonical picker instead of a text body. This is an EXPLICIT
// rejection of inline authoring for those subtypes, not a silent drop.

import { parseBlock, printBlock } from '../../engine/tslBlock/blockText';
import type { TslBlock } from '../../engine/tslBlock/ast';

const STARTER_TEMPLATES: Record<string, string> = {
  indicator: `indicator {
  param period: int 5..100 = 20
  value: ((close / ema(close, period)) - 1)
  signal: (((close / ema(close, period)) - 1) > 0)
}`,
  pattern: `pattern {
  signal: ((close > open) and (prev(close, 1) < prev(open, 1)) and (close > prev(open, 1)) and (open < prev(close, 1)))
}`,
  analysis: `analysis {
  param period: int 2..200 = 14
  value: rsi(close, period)
  signal: (rsi(close, period) > 50)
}`,
  filter: `filter {
  param minVolume: float 0..10000000 = 0
  signal: (volume >= minVolume)
}`,
  signal_generator: `signal_generator {
  signal: (close > prev(close, 1))
}`,
  qty_calculator: `qty_calculator {
  param frac: float 0.001..0.1 = 0.01
  qty: ((equity * frac) / entryPrice)
}`,
  risk_rule: `risk_rule {
  param maxBars: int 1..500 = 50
  risk: ((barsSinceEntry >= maxBars) ? 2 : 0)
}`,
};

// Every starter must parse — checked once at module load.
for (const [k, t] of Object.entries(STARTER_TEMPLATES)) {
  const r = parseBlock(t);
  if (!r.ok) throw new Error(`TSL starter template '${k}' does not parse: ${r.errors.map((e) => `line ${e.line}: ${e.error}`).join('; ')}`);
}

export function getStarterTemplate(subtype: string): string | null {
  return STARTER_TEMPLATES[subtype] ?? null;
}

/** True when the subtype can carry an inline safe-TSL body the engine
 *  actually executes (per-bar five via the interpreter's series path,
 *  qty_calculator via the sizing path, risk_rule via the checkBar path).
 *  sl/tp_calculator are canonical-marker-only. */
export function isTslAuthorableSubtype(subtype: string): boolean {
  return subtype in STARTER_TEMPLATES;
}

/** Canonical native implementations selectable for the canonical-only
 *  subtypes (mirrors src/engine/slTpCalculator.ts registries). */
export const CANONICAL_ONLY_OPTIONS: Record<string, Array<{ id: string; label: string }>> = {
  sl_calculator: [{ id: 'ftmo_calcsl_v1', label: 'FTMO swing-anchored ATR stop (ftmo_calcsl_v1)' }],
  tp_calculator: [{ id: 'ftmo_calctp_v1', label: 'FTMO R-multiple target (ftmo_calctp_v1)' }],
};

/** Default data payload for a freshly-added CUSTOM block (BlockCanvas /
 *  BlockEditor). TSL-native from the first keystroke: carries the parsed
 *  starter's tslText + tslAst + declared param defaults — never `code`.
 *  Returns a fresh object per call (block data is mutated by reducers). */
export function defaultCustomBlockData(): {
  name: string; blockType: CustomBlockSubtype; tslText: string; tslAst: TslBlock; parameters: Record<string, unknown>;
} {
  const r = parseBlock(STARTER_TEMPLATES.indicator);
  if (!r.ok) throw new Error('unreachable: starter validated at module load');
  const parameters: Record<string, unknown> = {};
  for (const [k, s] of Object.entries(r.block.params ?? {})) parameters[k] = s.default;
  return {
    name: 'Custom TSL Block',
    blockType: 'indicator' as CustomBlockSubtype,
    tslText: printBlock(r.block),
    tslAst: r.block,
    parameters,
  };
}
